import { privateApiClient } from './apiClient';

/**
 * Service pour gérer les retraits
 */

/**
 * Récupérer le solde d'un compte (même API que le dashboard : own + sol + crc)
 * @param {string} solution - ID de la solution (sol)
 * @param {string} currency - Devise (CDF ou USD) (crc)
 * @param {string} companyId - ID de l'organisation (own)
 * @returns {Promise<Object>} Solde du compte { balance, currency }
 */
export const fetchAccountBalance = async (solution, currency, companyId) => {
    const { data } = await privateApiClient.get('/trs/accounts', {
        params: {
            sol: solution,
            crc: currency,
            own: companyId
        }
    });

    return {
        balance: data?.balance ?? 0,
        currency: (data?.currency || currency || 'CDF').toUpperCase()
    };
};

/**
 * Récupérer les providers de retrait
 * @param {Object} params - Paramètres { pge, typ, opr, lmt, slug }
 * @returns {Promise<Array>} Liste des providers
 */
export const fetchWithdrawalProviders = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        typ: 'all',
        opr: 'WDL',
        lmt: 100,
        slug: '',
        ...params
    };

    const { data: response } = await privateApiClient.get('/trs/providers', { 
        params: defaultParams 
    });

    return response?.contents || [];
};

/**
 * Récupérer toutes les catégories disponibles
 * @returns {Promise<Array>} Liste des catégories
 */
export const fetchProviderCategories = async () => {
    const { data: response } = await privateApiClient.get('/usr/categories/all', {
        params: {
            pge: 1,
            lmt: 100,
            slug: ''
        }
    });

    return response.contents || [];
};

/**
 * Récupérer les types de providers pour les retraits
 * @returns {Promise<Array>} Liste des types
 */
export const fetchProviderTypes = async () => {
    const { data: response } = await privateApiClient.get('/trs/providers/provider-types', {
        params: {
            tgt: 'WDL'  // Sur mobile: tgt: "WDL"
        }
    });

    return response?.contents || [];
};

/**
 * Initier un retrait
 * @param {string} posId - ID du POS
 * @param {Object} data - Données { amount, currency }
 * @returns {Promise<Object>} Réponse avec URLs de callback
 */
export const initiateWithdrawal = async (posId, data) => {
    const { data: response } = await privateApiClient.post(`/pos/withdrawal/${posId}/initiate`, data);

    if (response.error) {
        throw new Error(response.message || 'Erreur lors de l\'initiation du retrait');
    }

    return response.contents;
};

/**
 * Créer une demande de retrait
 * @param {Object} withdrawalModel - Modèle complet du retrait { amount, currency, method, provider_id, solution_id, etc. }
 * @returns {Promise<Object>} Réponse de création
 */
export const createWithdrawalRequest = async (withdrawalModel) => {
    // L'OTP part avant la réponse : si le service tarde (notification interne lente), on attend
    // au-delà des 30 s par défaut pour récupérer trans_id et afficher la saisie de l'OTP.
    const { data: response } = await privateApiClient.post('/trs/transactions/requests/withdrawals', withdrawalModel, { timeout: 120000 });

    if (response.error) {
        throw new Error(response.message || 'Erreur lors de la création de la demande de retrait');
    }

    // Sur mobile, la réponse contient: { error, message, payload, expires_in, trans_id }
    return {
        error: response.error || false,
        message: response.message,
        payload: response.payload || response.contents || response,
        expires_in: response.expires_in,
        trans_id: response.trans_id
    };
};

/**
 * Confirmer un retrait
 * @param {Object} withdrawalModel - Modèle complet du retrait { owner, amount, currency, method, etc. }
 * @returns {Promise<Object>} Réponse de confirmation
 */
/**
 * Configuration de retrait de l'organisation (portée par le compte : account.withdrawSetting)
 * GET /trs/accounts?sol&crc&own
 */
export const fetchWithdrawSetting = async (solution, currency, companyId) => {
    const { data } = await privateApiClient.get('/trs/accounts', {
        params: { sol: solution, crc: currency, own: companyId }
    });
    return data?.withdrawSetting || null;
};

/** Politiques de retrait proposées (GET /trs/withdraw-policies), actives uniquement. */
export const fetchWithdrawPolicies = async () => {
    const { data } = await privateApiClient.get('/trs/withdraw-policies', {
        params: { pge: 1, lmt: 50, slg: '' }
    });
    const list = data?.contents || [];
    return list
        .filter((p) => p.enabled !== false)
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
};

/**
 * Enregistre la politique et le moyen de retrait (WithdrawSettingRqsDTO)
 * POST /trs/withdraw-policies
 */
export const saveWithdrawSetting = async (payload) => {
    try {
        const { data } = await privateApiClient.post('/trs/withdraw-policies', payload);
        if (data?.error) throw new Error(data.message || "Impossible d'enregistrer la politique de retrait");
        return data?.payload || data;
    } catch (error) {
        throw new Error(error?.response?.data?.message || error.message || "Impossible d'enregistrer la politique de retrait");
    }
};

/**
 * Renvoyer le code de confirmation d'une demande de retrait
 * GET /trs/transactions/requests/{trans_id}/resend-token
 */
export const resendWithdrawalToken = async (transId) => {
    try {
        const { data } = await privateApiClient.get(`/trs/transactions/requests/${transId}/resend-token`);
        if (data?.error) throw new Error(data.message || 'Impossible de renvoyer le code');
        return data;
    } catch (error) {
        throw new Error(error?.response?.data?.message || error.message || 'Impossible de renvoyer le code');
    }
};

export const confirmWithdrawal = async (withdrawalModel) => {
    const { data: response } = await privateApiClient.post('/trs/transactions/requests/confirm', withdrawalModel);

    if (response.error) {
        throw new Error(response.message || 'Erreur lors de la confirmation du retrait');
    }

    return response?.payload || response?.contents || response;
};

/**
 * Valider un IBAN
 * @param {string} iban - IBAN à valider
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateIBAN = (iban) => {
    const formattedIban = iban.replace(/\s+/g, '').toUpperCase();

    if (formattedIban.length < 15 || formattedIban.length > 34) {
        return { isValid: false, message: 'Longueur invalide pour un IBAN.' };
    }

    const rearrangedIban = formattedIban.slice(4) + formattedIban.slice(0, 4);
    const numericIban = rearrangedIban
        .split('')
        .map((char) => (char >= 'A' && char <= 'Z' ? char.charCodeAt(0) - 55 : char))
        .join('');

    try {
        // Version alternative sans BigInt
        let remainder = 0;
        for (let i = 0; i < numericIban.length; i++) {
            remainder = (remainder * 10 + parseInt(numericIban[i], 10)) % 97;
        }

        const isValid = remainder === 1;
        return {
            isValid,
            message: isValid ? 'IBAN valide.' : 'Clé de contrôle incorrecte.'
        };
    } catch (error) {
        return { isValid: false, message: 'Format IBAN invalide.' };
    }
};
